import { bool, func, node, number, object, string } from "prop-types";
import { forwardRef, useCallback, useState } from "react";

import styled, { css } from "styled-components/macro";

import FnrShineButton from "UI/FnrButton3/FnrShineButton";
import FnrButtonPopper from "UI/FnrButtonPopper";
import FnrSlides from "UI/FnrSlides";
import { colors, marketingColors } from "UI/colors";

import { countryCodeFromIDOrCode } from "Utilities/Address/networkCountryOptions";

import { VERIFICATION_PROVIDER } from "../hooks/useAddressAutocomplete";

import ChipGlyph from "./ChipGlyph";
import ChipLabel from "./ChipLabel";
import StaticPill from "./StaticPill";
import TogglePopover from "./TogglePopover";
import UspsVerifiedPill from "./UspsVerifiedPill";
import { chipBox, chipChrome } from "./chipPillStyle";

// The display states, keyed off the persisted verification metadata. `smarty`
// is the backend's audited USPS verdict (read-only here); `admin` is an admin's
// manual override; `unverified` is a recorded false verdict. `none` is the
// absence of a verdict — `isVerified` is not a boolean (null/undefined) — and
// renders no badge at all (see `applicable` below). An address that carries a
// lingering "smarty" provider but isVerified:false (the backend's USPS audit
// bounced it) correctly lands on `unverified` — it isn't verified.
const STATE = {
  usps: "usps",
  admin: "admin",
  unverified: "unverified",
  none: "none",
};

// Derive the badge's display state from a persisted address. Exported so a
// caller can decide whether to reserve a label-row slot without rendering the
// badge first (the profile read-only view does this).
const getAddressBadgeState = (value) => {
  // Verification is a US-only concept — a non-US address has NO verdict, so the
  // badge shows nothing (the feature-wide non-US OFF state: no chrome, no
  // verdict). Guard this FIRST: editing the country to non-US strips the
  // metadata to a literal isVerified:false, which would otherwise read as
  // STATE.unverified and render a bogus "Unverified" chip on a foreign address.
  if (countryCodeFromIDOrCode(value?.country || value?.countryID) !== "US")
    return STATE.none;
  // Only a real boolean verdict drives a badge: `isVerified` returning
  // null/undefined means the backend has no verdict to show.
  if (typeof value?.isVerified !== "boolean") return STATE.none;
  if (value.isVerified !== true) return STATE.unverified;
  if (value.verificationProvider === VERIFICATION_PROVIDER) return STATE.usps;
  return STATE.admin;
};

// The badge's render gate. An address with no boolean verdict (`STATE.none`)
// shows nothing; only managers (super admins on the profile; anyone in the
// Transfers tool there) see the admin-verified / unverified permutations — the
// "USPS Verified" badge is the backend's read-only audit verdict and stays
// visible to everyone; and an unverified address shows nothing on a
// non-editable surface. A caller can call this directly to keep its layout in
// lockstep with the badge (see the profile ReadOnly view).
const isAddressBadgeApplicable = ({ value, fallbackValue, canManage, editable }) => {
  const persisted = resolvePersisted(value, fallbackValue);
  const state = getAddressBadgeState(persisted);
  return (
    state !== STATE.none &&
    (canManage || state === STATE.usps) &&
    !(state === STATE.unverified && !editable)
  );
};

// Prefer the persisted/canonical address; fall back to the live buffer only for
// a brand-new address that hasn't been persisted yet (empty `value`). After a
// standardized save the buffer deliberately keeps the user's typed text while
// `value` holds the canonical address, so deriving from `value` keeps the badge
// state and the toggle's POST payload consistent with what's on screen.
const resolvePersisted = (value, fallbackValue) =>
  value && Object.keys(value).length > 0 ? value : fallbackValue;

// Per-state visuals for the two interactive states (`admin`, `unverified`):
// one informational shine-button chip that morphs between them. Their look is
// uniform (the icon + label say which state you're in). The `usps` state has no
// entry here — it's the backend's read-only verdict and renders the shared
// `UspsVerifiedPill` (which owns the eagle mark and its trademark scope), never
// the icon/label chip.
const CONFIG = {
  [STATE.admin]: {
    // A plain checkmark rendered as a tight ChipGlyph. The FA check's ink runs
    // ~1em tall — taller than the label's cap height — so `iconFontSize` caps it
    // to ~half the 12px chip base (≈6px), landing the glyph ink on the label's
    // cap height so it never rises above the text's top/bottom.
    icon: "check fas",
    iconFontSize: "0.5em",
    label: "Admin Verified",
    ariaLabel: "address-verified-admin",
    title: "Marked verified by an admin",
  },
  [STATE.unverified]: {
    // No icon — the unverified chip is gray and label-only (see `gray` below).
    icon: null,
    label: "Unverified",
    ariaLabel: "address-unverified",
    title: "Not verified — click to mark verified",
  },
};

// The morph row for an interactive state. FnrSlides morphs the shine button's
// contents between these when the address is toggled. fnBlue icon + the chip
// family's shared ChipLabel typography, matching the static pills exactly.
const SlideContent = ({ state }) => {
  const { icon, iconFontSize, label } = CONFIG[state];
  const iconless = !icon;
  return (
    <SlideRow $iconless={iconless}>
      {/* The unverified chip is icon-less (gray, label-only); only the admin
          state carries a glyph — a tight ChipGlyph (not FnrContainedIcon) capped
          to the label's cap height. `noGap` drops the label's icon-overlap
          margin: that margin overlapped FnrContainedIcon's container slack, which
          ChipGlyph doesn't have; the row owns the icon→label gap now. */}
      {icon && <ChipGlyph icon={icon} fontSize={iconFontSize} />}
      <ChipLabel noGap>{label}</ChipLabel>
    </SlideRow>
  );
};
SlideContent.propTypes = { state: string };

// One self-contained slide per interactive state (`admin`, `unverified`). It
// renders the toggle-popover chip on an editable surface and the equivalent
// static pill where the surface can't toggle — so the slide carries its OWN
// interactivity rather than the badge wrapping the slides in a popper. That's
// what lets the read-only `usps` pill live in the SAME slides container as the
// interactive states: every state is just a slide, so a verdict change
// (USPS → unverified after a re-edit, or the admin toggle) zoom-morphs in place
// instead of swapping one subtree for a structurally different one. The popover
// closes on toggle (`closeTrigger`), so a per-slide popper anchor is fine — the
// anchor never needs to survive the morph.
const InteractiveSlide = ({
  state,
  editable,
  verified,
  onToggle,
  closeTrigger,
  verifiedByName,
  verifiedBySelf,
}) => {
  const config = CONFIG[state];

  if (!editable) {
    return (
      <StaticPill
        icon={config.icon}
        iconFontSize={config.iconFontSize}
        label={config.label}
        ariaLabel={config.ariaLabel}
      />
    );
  }

  return (
    <FnrButtonPopper
      Button={BadgeShineButton}
      buttonProps={{
        ariaLabel: config.ariaLabel,
        title: config.title,
        $gray: state === STATE.unverified,
      }}
      closeTrigger={closeTrigger}
      color="white"
      content={
        <TogglePopover
          verified={verified}
          onToggle={onToggle}
          verifiedByName={verifiedByName}
          verifiedBySelf={verifiedBySelf}
        />
      }
    >
      <SlideContent state={state} />
    </FnrButtonPopper>
  );
};
InteractiveSlide.propTypes = {
  state: string,
  editable: bool,
  verified: bool,
  onToggle: func,
  closeTrigger: number,
  verifiedByName: string,
  verifiedBySelf: bool,
};

// The `usps` state is the backend's read-only verdict — never a toggle — so its
// slide is just the shared pill (which owns the eagle mark and its trademark
// scope).
const UspsSlide = () => <UspsVerifiedPill />;
const UnverifiedSlide = (props) => (
  <InteractiveSlide {...props} state={STATE.unverified} />
);
const AdminSlide = (props) => (
  <InteractiveSlide {...props} state={STATE.admin} />
);

// `usps` first so the slides container measures the read-only pill on mount (the
// common verified state); the interactive states only mount when they become
// active. Keyed by the same state strings `configKey` resolves to.
const SLIDE_COMPONENTS = [
  { key: STATE.usps, value: UspsSlide },
  { key: STATE.admin, value: AdminSlide },
  { key: STATE.unverified, value: UnverifiedSlide },
];

// Anchor shell FnrButtonPopper drives. It injects ref (the popover's anchor) +
// onClick (open/close) + showPopover; we wrap an FnrShineButton so the pill
// reads as informational until hovered, where the shine reveals it's clickable.
// ShineAnchor carries the chip chrome (the standardization-notice blues) and
// PillBox the chip's inner box (field-name-sized text, tight padding), so the
// interactive states match the static pills exactly.
const BadgeShineButton = forwardRef(
  (
    { ariaLabel, title, onClick, showPopover, children, $gray, ...rest },
    ref
  ) => (
    <ShineAnchor ref={ref} title={title} $gray={$gray} {...rest}>
      <FnrShineButton
        onClick={onClick}
        shineColor={marketingColors.taupe}
        borderRadius={999}
        aria-label={ariaLabel}
        type="button"
      >
        <PillBox>{children}</PillBox>
      </FnrShineButton>
    </ShineAnchor>
  )
);
BadgeShineButton.displayName = "BadgeShineButton";
BadgeShineButton.propTypes = {
  ariaLabel: string,
  title: string,
  onClick: func,
  showPopover: bool,
  children: node,
  $gray: bool,
};

/**
 * Address verification badge with three provider-driven states: USPS-verified
 * (read-only), admin-verified (toggle off), and unverified (toggle on). It is
 * prop-driven so both the admin profile (via a context adapter) and the
 * Transfers tool render the same chip + manual-verify toggle off the same code.
 *
 * Props:
 * - `value` — the PERSISTED/canonical address the badge derives its state from.
 * - `fallbackValue` — optional live edit buffer, used only when `value` is empty
 *   (a brand-new, never-persisted address). See `resolvePersisted`.
 * - `editable` — gates the toggle: non-editable surfaces show the verified
 *   states as static chips and nothing when unverified.
 * - `canManage` — who may see/toggle the manual states (super admin on the
 *   profile; anyone in the Transfers tool there). USPS stays visible regardless.
 * - `onSave(override)` — persists the toggled verification (fire-and-forget).
 * - `onOptimisticChange(override)` — mirrors the toggle into the caller's state
 *   for instant feedback before the save round-trips.
 * - `verifiedByName` / `verifiedBySelf` — credit shown in the toggle popover.
 */
const VerificationBadge = ({
  value,
  fallbackValue,
  editable,
  canManage,
  onSave,
  onOptimisticChange,
  verifiedByName,
  verifiedBySelf,
}) => {
  const persisted = resolvePersisted(value, fallbackValue);
  const state = getAddressBadgeState(persisted);
  const applicable = isAddressBadgeApplicable({
    value,
    fallbackValue,
    canManage,
    editable,
  });
  // Bumped on toggle to close the popover immediately.
  const [closeTrigger, setCloseTrigger] = useState(0);
  const persistedVerified = state !== STATE.unverified;

  // Optimistic verdict so the checkbox animates the instant it's clicked. The
  // badge's real state derives from `persisted` — which only updates after the
  // caller's state catches up, too late for the draw animation to play before
  // the popover closes. Hold an optimistic verdict until the persisted state
  // catches up, then drop it.
  const [optimisticVerified, setOptimisticVerified] = useState(null);
  const [prevPersistedVerified, setPrevPersistedVerified] =
    useState(persistedVerified);
  if (persistedVerified !== prevPersistedVerified) {
    setPrevPersistedVerified(persistedVerified);
    if (optimisticVerified !== null && persistedVerified === optimisticVerified)
      setOptimisticVerified(null);
  }
  const verified = optimisticVerified ?? persistedVerified;

  // Drive the interactive pill (admin vs unverified) and the button's label off
  // the optimistic verdict so the morph tracks the toggle in sync. USPS is the
  // read-only verdict and never optimistic, so it overrides the optimistic
  // display state. `configKey` is the active slide's key in SLIDE_COMPONENTS.
  const displayState = verified ? STATE.admin : STATE.unverified;
  const configKey = state === STATE.usps ? STATE.usps : displayState;

  // Toggle the admin override. Verified → admin provider; unverified strips the
  // metadata so the backend persists a clean unverified address. Mirror into
  // the caller's state for instant, optimistic feedback (no loader, no lag),
  // then POST the override fire-and-forget. Built off the persisted address
  // (never the typed buffer) so toggling verification changes only the
  // verification state and never mutates the address data — even when a pending
  // standardization-undo has left the buffer diverged from the canonical.
  const handleToggle = useCallback(() => {
    const nextVerified = !verified;
    setOptimisticVerified(nextVerified);
    const override = nextVerified
      ? { ...persisted, isVerified: true, verificationProvider: "admin" }
      : {
          ...persisted,
          isVerified: false,
          verificationProvider: "",
          verifiedAt: "",
        };
    onOptimisticChange?.(override);
    Promise.resolve(onSave?.(override)).catch(() => {});
    setCloseTrigger((n) => n + 1);
  }, [verified, persisted, onOptimisticChange, onSave]);

  if (!applicable) return null;

  // All three states are slides in ONE continuously-mounted FnrSlides, so a
  // verdict change zoom-morphs in place. The load-bearing reason: the read-only
  // USPS pill used to render as a structurally separate subtree from the
  // interactive states, so going USPS → unverified (a re-edit the backend
  // bounces) tore down one subtree and mounted a fresh FnrSlides at width 0 —
  // a jarring snap that flashed the badge narrow before it settled, reflowing
  // the title's ellipsis. With every state living in the same slides container,
  // the only thing that changes is `activeKey`, and width/scale animate between
  // the old and new pill. Each interactive slide owns its own toggle popper
  // (`editable` gates it), so the container never needs a wrapping popper.
  return (
    <BadgeMorph data-cy="AddressVerificationBadge">
      <FnrSlides
        components={SLIDE_COMPONENTS}
        activeKey={configKey}
        variant="zoom"
        clip={false}
        editable={editable}
        verified={verified}
        onToggle={handleToggle}
        closeTrigger={closeTrigger}
        verifiedByName={verifiedByName}
        verifiedBySelf={verifiedBySelf}
      />
    </BadgeMorph>
  );
};
VerificationBadge.propTypes = {
  value: object,
  fallbackValue: object,
  editable: bool,
  canManage: bool,
  onSave: func,
  onOptimisticChange: func,
  verifiedByName: string,
  verifiedBySelf: bool,
};

export { getAddressBadgeState, isAddressBadgeApplicable };
export default VerificationBadge;

// The morph container's host. inline-flex so the FnrSlides' block div sits on
// the title row like the old static pills did, and min-width:0 lets the zoom's
// width animation run without forcing the row wider than the badge's content.
//
// font-size: 0 collapses the FnrSlides wrappers' 16px-app-default strut, which
// otherwise made this item ~22px tall and baseline-dropped the pill ~2px below
// the field label beside it. Each slide's chrome resets font-size via chipChrome
// (12px), so only the empty wrapper struts collapse — the row's align-items:
// center then aligns the pill's box with the label.
const BadgeMorph = styled.span`
  display: inline-flex;
  min-width: 0;
  font-size: 0;
`;

// Inner morph row — pure layout. It must NOT carry the chip's padding: the box
// insets live once in chipBox (on PillBox below), so the badge can't drift from
// the static pills (an extra padding-right here was double-counting the right
// inset).
const SlideRow = styled.span`
  display: inline-flex;
  align-items: center;
  white-space: nowrap;
  gap: ${({ $iconless }) => ($iconless ? "0" : "2px")};
`;

// The chip's inner box — the shared chipBox owns its insets. The label
// typography lives in ChipLabel.
//
// `vertical-align: middle`: PillBox is inline-flex, so within the shine button's
// 12px-pinned line box it would otherwise baseline-align and sit ~1px low (most
// visible on the icon-less unverified chip). Middle-aligning centers it in that
// line box.
const PillBox = styled.span`
  ${chipBox}
  /* vertical-align: middle centers the inline-flex box in the shine button's
     line box; the 1px lift then optically centers the content — the shine
     button's 12px strut baseline-drops the box, leaving the all-caps label (and
     its cap-height glyph) ~1px low. Empirically derived for the xs label inside
     the 12px-pinned chrome. */
  vertical-align: middle;
  position: relative;
  bottom: 1px;
`;

// The chip's chrome lives on this outer shell (not PillBox) because the
// FnrShineButton sits between the two. chipChrome's font-size also pins the
// shine button's intermediate block wrappers to 12px, so their strut adds no
// line-box leading — without it this badge rendered 2px taller than the static
// pills.
const ShineAnchor = styled.span`
  display: inline-flex;
  /* The FnrSlides morph wrappers run at the 16px app-default font, so this
     inline-flex pill baseline-aligns in their line box and drops ~2px below the
     field label it sits beside. Middle-align it in that line box. */
  vertical-align: middle;
  ${chipChrome}
  ${({ $gray }) =>
    $gray &&
    css`
      background: ${colors.fnGray100};
      border-color: ${colors.fnGray200};
    `}
`;
